import { AlertCategory, CompanyRole, Privilege, UserRole } from '@/utils/enums';

import { baseApi } from './baseApi';
import { ApiTags, CommonTagIds, UserTagIds } from './tags';

export type AssignUserRequest = {
  companyId: string;
  roles: UserRole[];
};

export type AssignableUserDto = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
};

export type UserCompany = {
  id: string;
  name: string;
  companyRole: CompanyRole;
  roleName: string;
  isCompanyAdmin: boolean;
  userRoles: {
    id: UserRole;
    name: string;
  }[];
  // Resolved privileges for this company (from the backend UserRolePrivilegeCatalog).
  // Drives action-affordance gating in the UI. See useUserAccess().hasPrivilege.
  // Optional only so test fixtures needn't specify it; the backend always sends it.
  privileges?: Privilege[];
};

export type UserCompanyMembership = {
  id: string;
  companyId: string;
  companyName: string;
  isPrimaryCompany: boolean;
  roles: {
    id: UserRole;
    name: string;
  }[];
};

export type UserBasic = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  isPlatformAdmin: boolean;
  isTechnicalSupport: boolean;
  primaryCompanyId: string | null;
  primaryCompanyName: string | null;
  lastLogin: string | null;
};

export type UserFull = UserBasic & {
  phoneNumber: string;
  created: string;
};

export type CurrentUser = {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  isPlatformAdmin: boolean;
  isTechnicalSupport: boolean;
  isActive: boolean;
  lastLogin: string;
  created: string;
  companies: UserCompany[];
  primaryCompanyId: string;
  primaryCompanyName: string;
};

export type CreateUserRequest = {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  isPlatformAdmin: boolean;
  isTechnicalSupport: boolean;
  primaryCompanyId: string;
  userRoles: UserRole[];
};

export type UserSettings = {
  userId: string;
  emailNotificationsEnabled: boolean;
  cleansightMsaSigned: boolean;
  // The backend sends every category, and one the user never set reads true. Typed
  // partial so a reader falls back to true for a category missing from the response.
  alertCategories?: Partial<Record<AlertCategory, boolean>>;
};

export type UpdateUserSettingsRequest = {
  emailNotificationsEnabled?: boolean;
  cleansightMsaSigned?: boolean;
  // A category left out keeps its current setting
  alertCategories?: Partial<Record<AlertCategory, boolean>>;
};

export type UpdateUserDetailsRequest = {
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  primaryCompanyId?: string;
};

const BASE_URL = '/api/v1/users';

export const userApi = baseApi.injectEndpoints({
  endpoints: build => ({
    getCurrentUser: build.query<CurrentUser, void>({
      query: () => `${BASE_URL}/me`,
      providesTags: [{ type: ApiTags.USERS, id: UserTagIds.CURRENT_USER }],
    }),
    getUsers: build.query<UserBasic[], void>({
      query: () => BASE_URL,
      providesTags: [{ type: ApiTags.USERS, id: CommonTagIds.LIST }],
    }),
    getUserById: build.query<UserFull, string>({
      query: userId => `${BASE_URL}/${userId}`,
      providesTags: (result, error, userId) => [
        { type: ApiTags.USERS, id: userId },
      ],
    }),
    getUserMembershipsById: build.query<UserCompanyMembership[], string>({
      query: userId => `${BASE_URL}/${userId}/memberships`,
      providesTags: (result, error, userId) => [
        { type: ApiTags.USERS, id: `${userId}-${UserTagIds.MEMBERSHIPS}` },
      ],
    }),
    patchUserStatusById: build.mutation<
      UserBasic,
      { userId: string; isActive: boolean }
    >({
      query: ({ userId, isActive }) => ({
        url: `${BASE_URL}/${userId}/status`,
        method: 'PATCH',
        body: isActive.toString(),
      }),
      invalidatesTags: (result, error, { userId }) => [
        { type: ApiTags.USERS, id: CommonTagIds.LIST },
        { type: ApiTags.USERS, id: userId },
        { type: ApiTags.USERS, id: `${userId}-${UserTagIds.MEMBERSHIPS}` },
        ApiTags.COMPANIES,
      ],
    }),
    patchUserLastLogin: build.mutation<UserFull, void>({
      query: () => ({
        url: `${BASE_URL}/login`,
        method: 'PATCH',
      }),
      invalidatesTags: [{ type: ApiTags.USERS, id: UserTagIds.CURRENT_USER }],
    }),
    assignUserToCompany: build.mutation<
      UserCompanyMembership,
      { userId: string; data: AssignUserRequest }
    >({
      query: ({ userId, data }) => ({
        url: `${BASE_URL}/${userId}/memberships`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { userId }) => [
        { type: ApiTags.USERS, id: `${userId}-${UserTagIds.MEMBERSHIPS}` },
        ApiTags.COMPANIES,
      ],
    }),
    unassignUserFromCompany: build.mutation<
      UserCompanyMembership,
      { userId: string; membershipId: string }
    >({
      query: ({ userId, membershipId }) => ({
        url: `${BASE_URL}/${userId}/memberships/${membershipId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { userId }) => [
        { type: ApiTags.USERS, id: `${userId}-${UserTagIds.MEMBERSHIPS}` },
        ApiTags.COMPANIES,
      ],
    }),
    createUser: build.mutation<UserFull, CreateUserRequest>({
      query: data => ({
        url: BASE_URL,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: ApiTags.USERS, id: CommonTagIds.LIST }],
    }),
    getUserSettings: build.query<UserSettings, void>({
      query: () => `${BASE_URL}/settings`,
      providesTags: () => [
        { type: ApiTags.USERS, id: UserTagIds.USER_SETTINGS },
      ],
    }),
    patchUserSettings: build.mutation<UserSettings, UpdateUserSettingsRequest>({
      query: data => ({
        url: `${BASE_URL}/settings`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: [{ type: ApiTags.USERS, id: UserTagIds.USER_SETTINGS }],
    }),
    updateUserDetails: build.mutation<
      UserFull,
      { userId: string; data: UpdateUserDetailsRequest }
    >({
      query: ({ userId, data }) => ({
        url: `${BASE_URL}/${userId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { userId }) => [
        { type: ApiTags.USERS, id: CommonTagIds.LIST },
        { type: ApiTags.USERS, id: userId },
        { type: ApiTags.USERS, id: `${userId}-${UserTagIds.MEMBERSHIPS}` },
        { type: ApiTags.USERS, id: UserTagIds.CURRENT_USER },
      ],
    }),
    deleteUser: build.mutation<void, string>({
      query: userId => ({
        url: `${BASE_URL}/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, userId) => [
        { type: ApiTags.USERS, id: CommonTagIds.LIST },
        { type: ApiTags.USERS, id: userId },
        { type: ApiTags.USERS, id: `${userId}-${UserTagIds.MEMBERSHIPS}` },
        ApiTags.COMPANIES,
      ],
    }),
  }),
});

export const {
  useGetCurrentUserQuery,
  useGetUsersQuery,
  useGetUserByIdQuery,
  useGetUserMembershipsByIdQuery,
  usePatchUserStatusByIdMutation,
  usePatchUserLastLoginMutation,
  useAssignUserToCompanyMutation,
  useUnassignUserFromCompanyMutation,
  useCreateUserMutation,
  useGetUserSettingsQuery,
  usePatchUserSettingsMutation,
  useUpdateUserDetailsMutation,
  useDeleteUserMutation,
} = userApi;
